import React, { useState, useRef, useEffect } from 'react';
import { 
  X, 
  AlertTriangle, 
  Plus, 
  Trash2,
  Edit2,
  Check,
  RotateCcw
} from 'lucide-react';
import { Disturbance, DisturbanceCategory, Subcontroller } from '../types';

interface DisturbanceLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  disturbances: Disturbance[];
  onAddDisturbance: (dist: Disturbance) => void;
  onUpdateDisturbance?: (dist: Disturbance) => void;
  onDeleteDisturbance: (id: string) => void;
  currentProjectId?: string;
  projectName?: string;
  currentCampaignId?: string;
  campaignName?: string;
}

const CATEGORIES: DisturbanceCategory[] = [
  'Pump swaps',
  'Treaters',
  'Analyzer maintenance',
  'Compressor changes',
  'Feed changes',
  'Utility problems',
  'Product routing changes',
  'Weather impacts',
];

const getLocalIso = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

export const DisturbanceLogModal: React.FC<DisturbanceLogModalProps> = React.memo(({
  isOpen,
  onClose,
  disturbances,
  onAddDisturbance,
  onUpdateDisturbance,
  onDeleteDisturbance,
  currentProjectId,
  projectName,
  currentCampaignId,
  campaignName,
}) => {
  const activeProjectId = currentProjectId || currentCampaignId || 'kfs-apc-2026';
  const activeProjectName = projectName || campaignName || 'APC Project';

  const [category, setCategory] = useState<DisturbanceCategory>('Pump swaps');
  const [controller, setController] = useState<Subcontroller | 'Plant-Wide'>('Plant-Wide');
  const [timestamp, setTimestamp] = useState<string>(getLocalIso);
  const [description, setDescription] = useState<string>('');
  const [engineer, setEngineer] = useState<string>('J. Dhaliwal');
  const [editingDisturbance, setEditingDisturbance] = useState<Disturbance | null>(null);

  const timestampInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Reset edit state when modal opens or closes
  useEffect(() => {
    if (isOpen) {
      handleCancelEdit();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleStartEdit = (d: Disturbance) => {
    setEditingDisturbance(d);
    setCategory(d.category);
    setController(d.controller || 'Plant-Wide');
    const isoTime = d.timestamp.replace(' ', 'T');
    setTimestamp(isoTime);
    if (timestampInputRef.current) {
      timestampInputRef.current.value = isoTime;
    }
    setDescription(d.description);
    setEngineer(d.engineer);

    // Scroll form into view
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCancelEdit = () => {
    setEditingDisturbance(null);
    setCategory('Pump swaps');
    setController('Plant-Wide');
    const localIso = getLocalIso();
    setTimestamp(localIso);
    if (timestampInputRef.current) {
      timestampInputRef.current.value = localIso;
    }
    setDescription('');
    setEngineer('J. Dhaliwal');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) return;

    const chosenTimestamp = timestampInputRef.current?.value || timestamp;

    if (editingDisturbance) {
      // Editing existing disturbance - preserve ID, campaignId, and original createdAt
      const updatedDist: Disturbance = {
        ...editingDisturbance,
        timestamp: chosenTimestamp.replace('T', ' '),
        category,
        controller,
        description: description.trim(),
        engineer: engineer.trim(),
        updatedAt: new Date().toISOString(),
      };

      if (onUpdateDisturbance) {
        onUpdateDisturbance(updatedDist);
      } else {
        onAddDisturbance(updatedDist);
      }
      handleCancelEdit();
    } else {
      // New disturbance
      const newDist: Disturbance = {
        id: `dist-${Date.now()}`,
        campaignId: activeProjectId,
        timestamp: chosenTimestamp.replace('T', ' '),
        category,
        controller,
        description: description.trim(),
        engineer: engineer.trim(),
        createdAt: new Date().toISOString(),
      };

      onAddDisturbance(newDist);
      setDescription('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/40 backdrop-blur-xs overflow-y-auto">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden my-4 sm:my-8 animate-in fade-in-50 zoom-in-95 duration-150">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <div>
              <h2 className="font-semibold text-slate-900 text-base">Plant Disturbance Log</h2>
              <p className="text-[11px] text-slate-500">{activeProjectName} &bull; Process upsets &amp; operational changes</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
              title="Close dialog"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-5 max-h-[82vh] overflow-y-auto">
          {/* Disturbance Form (New or Edit) */}
          <form 
            ref={formRef}
            onSubmit={handleSubmit} 
            className={`p-4 border rounded-xl space-y-3 transition-all ${
              editingDisturbance
                ? 'bg-sky-50/40 border-sky-300 ring-2 ring-sky-500/10'
                : 'bg-slate-50 border-slate-200'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                {editingDisturbance ? (
                  <>
                    <Edit2 className="w-3.5 h-3.5 text-sky-600" />
                    <span className="text-sky-950 font-bold">Edit Disturbance Entry</span>
                    <span className="text-[10px] font-mono font-medium px-2 py-0.5 rounded bg-sky-100 text-sky-800 border border-sky-200">
                      {editingDisturbance.id}
                    </span>
                  </>
                ) : (
                  <>
                    <Plus className="w-3.5 h-3.5 text-sky-600" />
                    <span>Log New Plant Disturbance</span>
                  </>
                )}
              </div>

              {editingDisturbance && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="text-xs text-slate-500 hover:text-slate-800 font-medium hover:underline cursor-pointer flex items-center gap-1"
                >
                  <RotateCcw className="w-3 h-3" />
                  <span>Cancel Edit</span>
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2.5">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as DisturbanceCategory)}
                  className="w-full bg-white text-slate-900 text-xs border border-slate-300 rounded-lg p-2 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Unit</label>
                <select
                  value={controller}
                  onChange={(e) => setController(e.target.value as Subcontroller | 'Plant-Wide')}
                  className="w-full bg-white text-slate-900 text-xs border border-slate-300 rounded-lg p-2 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
                >
                  <option value="Plant-Wide">Plant-Wide</option>
                  <option value="Deethanizer">Deethanizer</option>
                  <option value="Frac 1">Frac 1</option>
                  <option value="Frac 2">Frac 2</option>
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-slate-600">Timestamp</label>
                  <button
                    type="button"
                    onClick={() => {
                      const localIso = getLocalIso();
                      setTimestamp(localIso);
                      if (timestampInputRef.current) {
                        timestampInputRef.current.value = localIso;
                      }
                    }}
                    className="text-[10px] text-sky-600 hover:text-sky-800 font-semibold cursor-pointer"
                  >
                    Reset to Now
                  </button>
                </div>
                <input
                  ref={timestampInputRef}
                  type="datetime-local"
                  defaultValue={timestamp}
                  key={timestamp}
                  onClick={(e) => {
                    try {
                      (e.currentTarget as any).showPicker?.();
                    } catch {}
                  }}
                  className="w-full bg-white text-slate-900 text-xs font-mono border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 cursor-pointer"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Engineer</label>
                <input
                  type="text"
                  value={engineer}
                  onChange={(e) => setEngineer(e.target.value)}
                  className="w-full bg-white text-slate-900 text-xs border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
              <textarea
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe pump swap, compressor surge, analyzer calibrate flag, feed swing..."
                className="w-full bg-white text-slate-900 text-xs border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
                required
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              {editingDisturbance ? (
                <>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="px-3.5 py-1.5 border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 font-medium text-xs rounded-lg transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-1.5 px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white font-semibold text-xs rounded-lg transition-colors shadow-xs cursor-pointer"
                  >
                    <Check className="w-3.5 h-3.5" />
                    <span>Save Changes</span>
                  </button>
                </>
              ) : (
                <button
                  type="submit"
                  className="flex items-center gap-1.5 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium text-xs rounded-lg transition-colors shadow-xs cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Log Disturbance</span>
                </button>
              )}
            </div>
          </form>

          {/* Disturbance Table */}
          <div>
            <div className="text-xs font-semibold text-slate-700 mb-2 flex items-center justify-between">
              <span>Logged Disturbances ({disturbances.length})</span>
              <span className="text-[11px] text-slate-400 font-normal">Click edit to modify existing entries</span>
            </div>

            <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-xs">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50 text-slate-600 border-b border-slate-200 text-[11px] font-mono uppercase">
                    <th className="py-2.5 px-3">Time</th>
                    <th className="py-2.5 px-3">Category</th>
                    <th className="py-2.5 px-3">Unit</th>
                    <th className="py-2.5 px-3">Description</th>
                    <th className="py-2.5 px-3">Engineer</th>
                    <th className="py-2.5 px-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-sans">
                  {disturbances.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-6 text-center text-slate-400">
                        No plant disturbances logged for this project.
                      </td>
                    </tr>
                  ) : (
                    disturbances.map((d) => {
                      const isEditing = editingDisturbance?.id === d.id;
                      return (
                        <tr 
                          key={d.id} 
                          className={`transition-colors ${
                            isEditing 
                              ? 'bg-sky-50/80 font-medium' 
                              : 'hover:bg-slate-50'
                          }`}
                        >
                          <td className="py-2.5 px-3 font-mono text-slate-600 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              {isEditing && (
                                <span className="w-2 h-2 rounded-full bg-sky-600 animate-pulse"></span>
                              )}
                              <span>{d.timestamp}</span>
                            </div>
                          </td>
                          <td className="py-2.5 px-3 whitespace-nowrap">
                            <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                              {d.category}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-slate-600 whitespace-nowrap font-mono text-[11px]">
                            {d.controller || 'Plant-Wide'}
                          </td>
                          <td className="py-2.5 px-3 text-slate-800">
                            {d.description}
                          </td>
                          <td className="py-2.5 px-3 text-slate-500 whitespace-nowrap">
                            {d.engineer}
                          </td>
                          <td className="py-2.5 px-3 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                onClick={() => handleStartEdit(d)}
                                className={`p-1.5 rounded-md transition-colors cursor-pointer ${
                                  isEditing
                                    ? 'text-sky-700 bg-sky-100 ring-1 ring-sky-400'
                                    : 'text-slate-400 hover:text-sky-600 hover:bg-sky-50'
                                }`}
                                title="Edit disturbance entry"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (window.confirm(`Are you sure you want to delete disturbance "${d.description}"?`)) {
                                    if (isEditing) handleCancelEdit();
                                    onDeleteDisturbance(d.id);
                                  }
                                }}
                                className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors cursor-pointer"
                                title="Delete disturbance"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
